import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sun, Moon, Archive, Trash2, FlaskConical, PackageX, AlertTriangle, CalendarClock
} from "lucide-react";

/**
 * ProductCard — visual card used in the Product Library grid.
 * Pure presentational component; all callbacks are injected.
 */
export const ProductCard = ({
  product,
  hasAnalyzedIngredients,
  getExpirationStatus,
  onSelect,
  onTogglePin,
  onAnalyze,
  onToggleShelve,
  onRequestDelete,
}) => {
  const isAnalyzed = hasAnalyzedIngredients(product);
  const isShelved = product.shelved;

  const skiniqScore = product.skiniq_score;
  const isBanned = product.is_banned ?? false;
  const hasScore = skiniqScore !== null && skiniqScore !== undefined;
  const isPinnedAm = product.is_pinned_am ?? false;
  const isPinnedPm = product.is_pinned_pm ?? false;

  const exp = getExpirationStatus(product);
  const expCls = !exp ? "" : exp.kind === "expired"
    ? "bg-rose-100 text-rose-700"
    : exp.kind === "soon"
      ? "bg-amber-100 text-amber-700"
      : exp.kind === "watch"
        ? "bg-yellow-50 text-yellow-700"
        : "bg-emerald-50 text-emerald-700";

  return (
    <div
      className={`relative p-4 border-2 rounded-xl shadow-sm bg-white cursor-pointer hover:shadow-md hover:border-primary/30 transition-all ${isShelved ? "opacity-60 bg-gray-50" : ""}`}
      onClick={() => onSelect(product)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Header */}
      <div className="mb-3">
        <h3 className={`text-lg font-bold leading-tight truncate ${isShelved ? "text-gray-500" : "text-primary"}`}>
          {product.name}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          {product.brand && (
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{product.brand}</p>
          )}
          {!isShelved && hasScore && (
            <span className={`px-2 py-0.5 rounded-md font-black text-white text-sm ${
              skiniqScore >= 85 ? "bg-emerald-500" :
              skiniqScore >= 65 ? "bg-amber-500" : "bg-rose-500"
            }`}>
              {skiniqScore}
            </span>
          )}
          {!isShelved && !hasScore && (
            <span className="px-2 py-0.5 rounded-md font-medium text-gray-500 text-xs bg-gray-100">N/A</span>
          )}
          {!isShelved && (
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={(e) => onTogglePin(product.id, "am", isPinnedAm, e)}
                className={`p-1 rounded-full transition-all ${
                  isPinnedAm
                    ? "bg-amber-100 text-amber-500 shadow-sm shadow-amber-200"
                    : "text-gray-300 hover:text-amber-400 hover:bg-amber-50"
                }`}
                title={isPinnedAm ? "Unpin from AM routine" : "Pin to AM routine"}
              >
                <Sun className={`w-4 h-4 ${isPinnedAm ? "fill-amber-200" : ""}`} />
              </button>
              <button
                onClick={(e) => onTogglePin(product.id, "pm", isPinnedPm, e)}
                className={`p-1 rounded-full transition-all ${
                  isPinnedPm
                    ? "bg-indigo-100 text-indigo-500 shadow-sm shadow-indigo-200"
                    : "text-gray-300 hover:text-indigo-400 hover:bg-indigo-50"
                }`}
                title={isPinnedPm ? "Unpin from PM routine" : "Pin to PM routine"}
              >
                <Moon className={`w-4 h-4 ${isPinnedPm ? "fill-indigo-200" : ""}`} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {product.category && (
          <Badge variant="outline" className="text-xs capitalize">{product.category}</Badge>
        )}
        {isShelved && (
          <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-600">
            <PackageX className="w-3 h-3 mr-1" />
            Shelved
          </Badge>
        )}
        {!isAnalyzed && !isShelved && (
          <Badge
            variant="secondary"
            className="text-xs bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200 transition-colors"
            onClick={(e) => { e.stopPropagation(); onAnalyze(product.id); }}
            title="Click to analyze ingredients"
          >
            <FlaskConical className="w-3 h-3 mr-1" />
            Analyze
          </Badge>
        )}
        {exp && !isShelved && (
          <Badge variant="secondary" className={`text-xs ${expCls}`} title={`Expires ${product.expiration_date}`}>
            <CalendarClock className="w-3 h-3 mr-1" />
            {exp.label}
          </Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        {product.ingredients?.length || 0} ingredients
      </p>

      {isBanned && !isShelved && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Contains EU Banned Ingredients
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1 pt-3 border-t border-gray-100">
        <Button
          variant="ghost"
          size="sm"
          className={`flex-1 h-8 text-xs transition-all duration-150 ${
            isShelved
              ? "text-primary hover:bg-primary hover:text-white"
              : "text-muted-foreground hover:bg-pink-500 hover:text-white"
          }`}
          onClick={(e) => { e.stopPropagation(); onToggleShelve(product.id, product.shelved); }}
          data-testid={`shelve-product-${product.id}`}
        >
          <Archive className="w-4 h-4 mr-1" />
          {isShelved ? "Unshelve" : "Shelve"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground hover:text-white hover:bg-red-500 transition-all duration-150"
          onClick={(e) => { e.stopPropagation(); onRequestDelete(product.id); }}
          data-testid={`delete-product-${product.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
